import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../hooks/useCart';
import { useAuth } from '../../hooks/use-auth.js';
import { addressService, orderService } from '../../api';
import { useToast } from '../../hooks/use-toast';
import { openWhatsAppOrder } from '../../utils';
import { useStore } from '../../contexts/StoreContext';
import Button from '../../components/ui/ButtonDash';
import OrderConfirmation from '../../components/ui/OrderConfirmation';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * Checkout - Página de finalização de pedido da aplicação Conecta-Loja
 *
 * Página completa e moderna para finalização de pedidos com experiência fluida
 * e confirmação animada tipo PicPay. Gerencia todo o fluxo de checkout desde
 * a seleção de endereço até a confirmação final do pedido.
 *
 * @component
 *
 * Fluxo de checkout completo:
 * 1. Verifica se usuário tem endereços cadastrados
 * 2. Se não tiver, permite cadastro de endereço diretamente na página
 * 3. Permite seleção do endereço de entrega
 * 4. Mostra resumo detalhado do pedido
 * 5. Permite finalizar pedido com confirmação animada
 * 6. Redireciona para WhatsApp para confirmação de pagamento
 *
 * Estados gerenciados:
 * - Lista completa de endereços do usuário
 * - Endereço selecionado para entrega
 * - Estados de loading (autenticação, endereços, criação de pedido)
 * - Modal de confirmação animada do pedido
 * - Formulário de cadastro de endereço integrado
 *
 * Integrações principais:
 * - useCart: Acesso aos itens do carrinho e funções de limpeza
 * - useAuth: Dados do usuário autenticado
 * - addressService: Gerenciamento completo de endereços
 * - orderService: Criação e processamento de pedidos
 * - OrderConfirmation: Componente de confirmação animada
 *
 * Funcionalidades especiais:
 * - Confirmação animada tipo PicPay com ícone de sucesso
 * - Redirecionamento automático para WhatsApp
 * - Limpeza automática do carrinho após confirmação
 * - Navegação para histórico de pedidos
 * - Tratamento robusto de erros com toast notifications
 *
 * @example
 * // Rota configurada em Routes.jsx
 * <Route path="/checkout" element={<Checkout />} />
 *
 * @example
 * // Navegação via carrinho
 * navigate('/checkout');
 */
const Checkout = () => {
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [creatingOrder, setCreatingOrder] = useState(false);

  // Hook da loja para informações dinâmicas
  const { storeInfo } = useStore();
  const [showAddAddress, setShowAddAddress] = useState(false);

  // Estados para confirmação do pedido
  const [showOrderSuccessModal, setShowOrderSuccessModal] = useState(false);
  const [confirmedOrderData, setConfirmedOrderData] = useState(null);

  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { items, totalPrice, totalItems, clearCart, closeCart } = useCart();
  const { toast } = useToast();


  /**
   * Carrega todos os endereços cadastrados do usuário autenticado
   *
   * Faz uma chamada para a API para buscar os endereços do usuário,
   * atualiza o estado local e seleciona automaticamente o primeiro
   * endereço como padrão se existir.
   *
   * @async
   * @function loadAddresses
   * @returns {Promise<void>} Promise que resolve quando os endereços são carregados
   *
   * @throws {Error} Quando há erro na comunicação com a API
   *
   * @example
   * await loadAddresses();
   */
  const loadAddresses = async () => {
    try {
      setLoadingAddresses(true);
      const response = await addressService.getUserAddresses();
      if (response.success) {
        setAddresses(response.addresses || []);
        // Seleciona automaticamente o primeiro endereço se existir
        if (response.addresses && response.addresses.length > 0) {
          setSelectedAddressId(response.addresses[0].id);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar endereços:', error);
      toast({
        variant: "destructive",
        title: "Erro ao carregar endereços",
        description: "Não foi possível carregar seus endereços. Tente novamente."
      });
    } finally {
      setLoadingAddresses(false);
    }
  };

  /**
   * Verifica os pré-requisitos necessários para prosseguir com o checkout
   *
   * Valida se o usuário está autenticado e se há itens no carrinho.
   * Se alguma validação falhar, mostra toast de erro e redireciona
   * para a página apropriada.
   *
   * @function checkCheckoutPrerequisites
   * @returns {boolean} true se pode prosseguir, false caso contrário
   *
   * @example
   * if (!checkCheckoutPrerequisites()) {
   *   return; // Interrompe o fluxo
   * }
   */
  const checkCheckoutPrerequisites = () => {
    // Verifica se usuário está logado
    if (!user || !user.id) {
      toast({
        variant: "destructive",
        title: "Login necessário",
        description: "Você precisa estar logado para finalizar pedidos."
      });
      navigate('/');
      return false;
    }

    // Verifica se há itens no carrinho
    if (!items || items.length === 0) {
      toast({
        variant: "destructive",
        title: "Carrinho vazio",
        description: "Adicione produtos ao carrinho antes de finalizar."
      });
      navigate('/');
      return false;
    }

    return true;
  };

  /**
   * Formata um endereço completo para exibição em formato legível
   *
   * Combina todos os campos do endereço em uma string formatada,
   * incluindo logradouro, número, complemento, bairro, cidade, estado e CEP.
   *
   * @function formatAddress
   * @param {Object} address - Objeto contendo os dados do endereço
   * @param {string} address.logradouro - Nome da rua/avenida
   * @param {string} address.numero - Número do endereço
   * @param {string} [address.complemento] - Complemento (opcional)
   * @param {string} address.bairro - Bairro/distrito
   * @param {string} address.cidade - Cidade
   * @param {string} address.estado - Estado (UF)
   * @param {string} address.cep - CEP formatado
   * @returns {string} Endereço formatado para exibição
   *
   * @example
   * const endereco = {
   *   logradouro: "Rua das Flores",
   *   numero: "123",
   *   complemento: "Apto 45",
   *   bairro: "Centro",
   *   cidade: "São Paulo",
   *   estado: "SP",
   *   cep: "01234-567"
   * };
   * const enderecoFormatado = formatAddress(endereco);
   * // Resultado: "Rua das Flores, 123, Apto 45 - Centro, São Paulo - SP, CEP: 01234-567"
   */
  const formatAddress = (address) => {
    return `${address.logradouro}, ${address.numero}${address.complemento ? `, ${address.complemento}` : ''} - ${address.bairro}, ${address.cidade} - ${address.estado}, CEP: ${address.cep}`;
  };

  /**
   * Manipula a seleção de um endereço de entrega
   *
   * Atualiza o estado do endereço selecionado quando o usuário
   * clica em um endereço na lista de opções.
   *
   * @function handleAddressSelect
   * @param {number|string} addressId - ID do endereço selecionado
   *
   * @example
   * <div onClick={() => handleAddressSelect(address.id)}>
   *   Endereço clicável
   * </div>
   */
  const handleAddressSelect = (addressId) => {
    setSelectedAddressId(addressId);
  };

  /**
   * Controla a visibilidade do formulário de adicionar endereço
   *
   * Alterna entre mostrar/esconder o formulário integrado de cadastro
   * de novo endereço na página de checkout.
   *
   * @function handleAddAddress
   *
   * @example
   * <Button onClick={handleAddAddress}>
   *   {showAddAddress ? 'Cancelar' : 'Adicionar Endereço'}
   * </Button>
   */
  const handleAddAddress = () => {
    setShowAddAddress(!showAddAddress);
  };

  /**
   * Salva um novo endereço de entrega para o usuário
   *
   * Processa o cadastro de um novo endereço através da API,
   * atualiza a lista de endereços e fecha o formulário em caso de sucesso.
   * Mostra notificações toast para feedback do usuário.
   *
   * @async
   * @function handleSaveAddress
   * @param {Object} addressData - Dados do endereço a ser cadastrado
   * @param {string} addressData.cep - CEP do endereço
   * @param {string} addressData.logradouro - Nome da rua/avenida
   * @param {string} addressData.numero - Número do endereço
   * @param {string} [addressData.complemento] - Complemento (opcional)
   * @param {string} [addressData.informacoes_adicionais] - Informações adicionais (opcional)
   * @param {string} addressData.bairro - Bairro/distrito
   * @param {string} addressData.cidade - Cidade
   * @param {string} addressData.estado - Estado (UF)
   * @returns {Promise<void>} Promise que resolve quando o endereço é salvo
   *
   * @throws {Error} Quando há erro na validação ou comunicação com a API
   *
   * @example
   * const endereco = {
   *   cep: "01234-567",
   *   logradouro: "Rua das Flores",
   *   numero: "123",
   *   bairro: "Centro",
   *   cidade: "São Paulo",
   *   estado: "SP"
   * };
   * await handleSaveAddress(endereco);
   */
  const handleSaveAddress = async (addressData) => {
    try {
      const response = await addressService.createAddress(addressData);
      if (response.success) {
        toast({
          title: "Endereço adicionado!",
          description: "Endereço cadastrado com sucesso.",
          variant: "default"
        });
        // Recarrega endereços e seleciona o novo
        await loadAddresses();
        setShowAddAddress(false);
      }
    } catch (error) {
      toast({
        title: "Erro ao adicionar endereço",
        description: error.message || "Não foi possível adicionar o endereço.",
        variant: "destructive"
      });
    }
  };


  /**
   * Finaliza e confirma o pedido do usuário
   *
   * Processo completo de finalização do pedido:
   * 1. Formata os produtos do carrinho para o formato da API
   * 2. Cria o pedido através da API do backend
   * 3. Prepara dados para WhatsApp (cliente, produtos, pedido)
   * 4. Mostra modal de confirmação animada tipo PicPay
   * 5. Trata erros e mostra notificações apropriadas
   *
   * Após a confirmação animada, o pedido é enviado para WhatsApp
   * e o carrinho é limpo automaticamente.
   *
   * @async
   * @function handleConfirmOrder
   * @returns {Promise<void>} Promise que resolve quando o pedido é processado
   *
   * @throws {Error} Quando há erro na criação do pedido ou comunicação com APIs
   *
   * Fluxo de execução:
   * - Validação de dados do usuário e endereço
   * - Formatação de produtos para API
   * - Criação do pedido no backend
   * - Preparação de dados para WhatsApp
   * - Exibição de confirmação animada
   * - Redirecionamento e limpeza do carrinho
   *
   * @example
   * // Chamado quando usuário clica em "Confirmar Pedido"
   * <Button onClick={handleConfirmOrder}>
   *   Confirmar Pedido
   * </Button>
   */
  const handleConfirmOrder = async () => {
    try {
      setCreatingOrder(true);

      // Formatar os produtos do carrinho para o formato esperado pela API
      const produtos = items.map(item => ({
        produtoId: item.product.id,
        quantidade: item.quantity,
        precoUnitario: item.product.price
      }));

      // Validação final de estoque antes de criar o pedido
      for (const item of items) {
        const requestedQuantity = item.quantity;
        const availableStock = item.product?.estoque || 0;

        if (requestedQuantity > availableStock) {
          throw new Error(`Estoque insuficiente para "${item.product?.name}". Disponível: ${availableStock}, solicitado: ${requestedQuantity}`);
        }
      }

      // Criar o pedido
      const orderData = {
        usuarioId: user.id,
        enderecoId: selectedAddressId, // Adiciona o endereço selecionado
        produtos: produtos,
        precoTotal: totalPrice,
        status: 'RECEBIDO'
      };

      const result = await orderService.createOrder(orderData);

      if (result.success) {
        // Preparar dados para WhatsApp
        const orderData = {
          id: result.pedido?.id,
          numeroPedido: result.pedido?.numeroPedido
        };

        // Garantir que o nome seja tratado corretamente (problema de encoding)
        const cleanName = (user.name || 'Cliente').replace(/Ã©/g, 'é').replace(/Ã¡/g, 'á').replace(/Ã£/g, 'ã').replace(/Ãµ/g, 'õ').replace(/Ã§/g, 'ç').replace(/Ã‰/g, 'É').replace(/Ã/g, 'Á');

        const customerData = {
          name: cleanName,
          phone: user.contact || user.phone || null // Tentar contact ou phone
        };

        // Formatar itens do carrinho para WhatsApp
        const whatsappItems = items.map(item => {
          const price = parseFloat(item.product?.price) || 0;
          return {
            name: item.product?.name || 'Produto sem nome',
            quantity: item.quantity || 1,
            price: price
          };
        });

        // Salvar dados para a confirmação animada
        setConfirmedOrderData({
          orderData,
          customerData,
          whatsappItems
        });

        // Mostrar confirmação animada
        setShowOrderSuccessModal(true);
      } else {
        toast({
          title: 'Erro ao criar pedido',
          description: 'Ocorreu um erro ao processar seu pedido. Tente novamente.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Erro ao finalizar pedido:', error);
      toast({
        title: 'Erro inesperado',
        description: 'Ocorreu um erro ao finalizar seu pedido. Verifique sua conexão e tente novamente.',
        variant: 'destructive'
      });
    } finally {
      setCreatingOrder(false);
    }
  };

  /**
   * Manipula a conclusão da animação de confirmação do pedido
   *
   * Executado quando a animação do OrderConfirmation termina.
   * Abre o WhatsApp com os dados preparados, limpa o carrinho
   * e navega para a página de histórico de pedidos.
   *
   * @function handleOrderConfirmationComplete
   * @returns {void}
   *
   * Sequência de ações:
   * 1. Abre WhatsApp com dados do pedido e cliente
   * 2. Aguarda 500ms para estabilização
   * 3. Limpa o carrinho completamente
   * 4. Fecha a sidebar do carrinho
   * 5. Navega para página de histórico
   *
   * @example
   * // Chamado automaticamente pelo componente OrderConfirmation
   * <OrderConfirmation onComplete={handleOrderConfirmationComplete} />
   */
  const handleOrderConfirmationComplete = () => {
    if (confirmedOrderData) {
      const { orderData, customerData, whatsappItems } = confirmedOrderData;

      // Número do WhatsApp da loja (dinâmico)
      const storePhoneNumber = storeInfo?.contact;

      // Abrir WhatsApp com número da loja
      openWhatsAppOrder(orderData, customerData, whatsappItems, storePhoneNumber);

      // Limpar carrinho e fechar após redirecionamento
      setTimeout(() => {
        clearCart();
        closeCart();
        navigate('/history');
      }, 500);
    }
  };

  /**
   * Navega de volta para a página do carrinho
   *
   * Permite ao usuário voltar para a página anterior (geralmente o carrinho)
   * para fazer alterações nos itens do pedido antes da finalização.
   *
   * @function handleBackToCart
   * @returns {void}
   *
   * @example
   * // Botão de voltar no breadcrumb
   * <button onClick={handleBackToCart}>
   *   ← Voltar ao Carrinho
   * </button>
   */
  const handleBackToCart = () => {
    navigate(-1); // Volta para a página anterior
  };

  // Carrega dados na inicialização
  useEffect(() => {
    // Só executa quando a autenticação terminou de carregar
    if (authLoading) return;

    if (!checkCheckoutPrerequisites()) return;

    loadAddresses();
    setLoading(false);
  }, [authLoading]);

  // Seleciona automaticamente o primeiro endereço quando carregados
  useEffect(() => {
    if (!loadingAddresses && addresses.length > 0 && !selectedAddressId) {
      setSelectedAddressId(addresses[0].id);
    }
  }, [loadingAddresses, addresses, selectedAddressId]);


  // Renderiza loading
  if (authLoading || loading || loadingAddresses) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando checkout...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <nav className="flex items-center space-x-2 text-sm text-muted-foreground">
            <button
              onClick={handleBackToCart}
              className="hover:text-foreground transition-colors"
            >
              ← Voltar ao Carrinho
            </button>
            <span>/</span>
            <span className="text-foreground font-medium">Checkout</span>
          </nav>
        </div>

        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-foreground mb-2">Finalizar Pedido</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Confirme os detalhes do seu pedido e selecione o endereço de entrega
          </p>
        </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {/* Itens do Pedido */}
            <div className="xl:col-span-2 space-y-6">
              {/* Resumo do Pedido */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">Resumo do Pedido</h2>

                <div className="space-y-4">
                  {items.map((item) => (
                    <div key={item.product.id} className="flex items-center space-x-4">
                      <img
                        src={item.product.image || '/images/placeholder.png'}
                        alt={item.product.name}
                        className="w-16 h-16 object-cover rounded-md"
                      />
                      <div className="flex-1">
                        <h3 className="font-medium text-foreground">{item.product.name}</h3>
                        <p className="text-sm text-muted-foreground">{item.product.description}</p>
                        <p className="text-sm text-muted-foreground">Quantidade: {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-foreground">
                          R$ {(item.product.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-border mt-6 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-foreground">Total:</span>
                    <span className="text-2xl font-bold text-primary">
                      R$ {totalPrice.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Seleção de Endereço */}
              <div className="bg-card rounded-lg border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-foreground">Endereço de Entrega</h2>
                  {!showAddAddress && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddAddress}
                      iconName="Plus"
                      iconPosition="left"
                    >
                      Adicionar Endereço
                    </Button>
                  )}
                </div>

                {/* Formulário de adicionar endereço */}
                {showAddAddress && (
                  <div className="border border-border rounded-lg p-4 mb-4 bg-background/50">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-foreground">Novo Endereço</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowAddAddress(false)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        ✕
                      </Button>
                    </div>

                    <AddressForm onSave={handleSaveAddress} onCancel={() => setShowAddAddress(false)} />
                  </div>
                )}

                {/* Lista de endereços ou mensagem quando vazio */}
                {addresses.length === 0 && !showAddAddress ? (
                  <div className="text-center py-8">
                    <div className="mb-4">
                      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                        <span className="text-2xl">📍</span>
                      </div>
                      <h3 className="text-lg font-medium text-foreground mb-2">
                        Nenhum endereço cadastrado
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        Você precisa cadastrar um endereço para finalizar seu pedido.
                      </p>
                    </div>
                    <Button onClick={handleAddAddress} className="w-full">
                      Cadastrar Primeiro Endereço
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {addresses.map((address) => (
                      <div
                        key={address.id}
                        className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                          selectedAddressId === address.id
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                        onClick={() => handleAddressSelect(address.id)}
                      >
                        <div className="flex items-start space-x-3">
                          <input
                            type="radio"
                            checked={selectedAddressId === address.id}
                            onChange={() => handleAddressSelect(address.id)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-foreground">
                              {formatAddress(address)}
                            </p>
                            {address.informacoes_adicionais && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {address.informacoes_adicionais}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Resumo Lateral */}
            <div className="space-y-6">
              {/* Resumo do Pedido */}
              <div className="bg-card rounded-lg border border-border p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Resumo</h3>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Itens ({totalItems}):</span>
                    <span className="text-foreground">R$ {totalPrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Entrega:</span>
                    <span className="text-foreground">A calcular</span>
                  </div>
                  <div className="border-t border-border pt-2">
                    <div className="flex justify-between">
                      <span className="text-lg font-semibold text-foreground">Total:</span>
                      <span className="text-lg font-bold text-primary">
                        R$ {totalPrice.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  <Button
                    onClick={handleConfirmOrder}
                    disabled={!selectedAddressId || creatingOrder || addresses.length === 0}
                    loading={creatingOrder}
                    className="w-full"
                  >
                    {creatingOrder ? 'Finalizando Pedido...' : 'Confirmar Pedido'}
                  </Button>

                  <Button
                    variant="outline"
                    onClick={handleBackToCart}
                    className="w-full"
                  >
                    Voltar ao Carrinho
                  </Button>
                </div>
              </div>
            </div>
          </div>
      </div>

      {/* Order Confirmation Modal */}
      <OrderConfirmation
        isVisible={showOrderSuccessModal}
        orderNumber={confirmedOrderData?.orderData?.numeroPedido || confirmedOrderData?.orderData?.id}
        onComplete={handleOrderConfirmationComplete}
      />
    </div>
  );
};

/**
 * AddressForm - Formulário integrado para cadastro de endereços no checkout
 *
 * Componente auxiliar do Checkout que permite ao usuário cadastrar
 * um novo endereço de entrega diretamente na página de finalização
 * do pedido, sem precisar navegar para outra página.
 *
 * @component
 * @param {Object} props - Propriedades do componente
 * @param {Function} props.onSave - Callback executado quando endereço é salvo com sucesso
 * @param {Function} props.onCancel - Callback executado quando usuário cancela o cadastro
 *
 * Campos do formulário:
 * - CEP (com validação de formato)
 * - Estado (select com todas UFs brasileiras)
 * - Logradouro (rua/avenida)
 * - Número do endereço
 * - Complemento (opcional)
 * - Bairro
 * - Cidade
 * - Informações adicionais (ponto de referência)
 *
 * Funcionalidades:
 * - Validação obrigatória de campos principais
 * - Estados de loading durante salvamento
 * - Reset automático do formulário após sucesso
 * - Tratamento de erros com feedback visual
 *
 * @example
 * <AddressForm
 *   onSave={(addressData) => handleSaveAddress(addressData)}
 *   onCancel={() => setShowAddAddress(false)}
 * />
 */
const AddressForm = ({ onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    informacoes_adicionais: '',
    bairro: '',
    cidade: '',
    estado: ''
  });
  const [saving, setSaving] = useState(false);

  /**
   * Manipula mudanças nos campos do formulário
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  /**
   * Processa o envio do formulário de endereço
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await onSave(formData);
      // Reset form
      setFormData({
        cep: '',
        logradouro: '',
        numero: '',
        complemento: '',
        informacoes_adicionais: '',
        bairro: '',
        cidade: '',
        estado: ''
      });
    } catch (error) {
      // Error is handled in parent component
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* CEP */}
        <div className="space-y-2">
          <Label htmlFor="cep">CEP *</Label>
          <Input
            id="cep"
            name="cep"
            type="text"
            placeholder="00000-000"
            value={formData.cep}
            onChange={handleChange}
            required
          />
        </div>

        {/* Estado */}
        <div className="space-y-2">
          <Label htmlFor="estado">Estado *</Label>
          <select
            id="estado"
            name="estado"
            value={formData.estado}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
            required
          >
            <option value="">Selecione...</option>
            <option value="AC">Acre</option>
            <option value="AL">Alagoas</option>
            <option value="AP">Amapá</option>
            <option value="AM">Amazonas</option>
            <option value="BA">Bahia</option>
            <option value="CE">Ceará</option>
            <option value="DF">Distrito Federal</option>
            <option value="ES">Espírito Santo</option>
            <option value="GO">Goiás</option>
            <option value="MA">Maranhão</option>
            <option value="MT">Mato Grosso</option>
            <option value="MS">Mato Grosso do Sul</option>
            <option value="MG">Minas Gerais</option>
            <option value="PA">Pará</option>
            <option value="PB">Paraíba</option>
            <option value="PR">Paraná</option>
            <option value="PE">Pernambuco</option>
            <option value="PI">Piauí</option>
            <option value="RJ">Rio de Janeiro</option>
            <option value="RN">Rio Grande do Norte</option>
            <option value="RS">Rio Grande do Sul</option>
            <option value="RO">Rondônia</option>
            <option value="RR">Roraima</option>
            <option value="SC">Santa Catarina</option>
            <option value="SP">São Paulo</option>
            <option value="SE">Sergipe</option>
            <option value="TO">Tocantins</option>
          </select>
        </div>
      </div>

      {/* Logradouro */}
      <div className="space-y-2">
        <Label htmlFor="logradouro">Logradouro *</Label>
        <Input
          id="logradouro"
          name="logradouro"
          type="text"
          placeholder="Rua, Avenida, etc."
          value={formData.logradouro}
          onChange={handleChange}
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Número */}
        <div className="space-y-2">
          <Label htmlFor="numero">Número *</Label>
          <Input
            id="numero"
            name="numero"
            type="text"
            placeholder="123"
            value={formData.numero}
            onChange={handleChange}
            required
          />
        </div>

        {/* Complemento */}
        <div className="space-y-2">
          <Label htmlFor="complemento">Complemento</Label>
          <Input
            id="complemento"
            name="complemento"
            type="text"
            placeholder="Apto, Bloco, etc."
            value={formData.complemento}
            onChange={handleChange}
          />
        </div>

        {/* Bairro */}
        <div className="space-y-2">
          <Label htmlFor="bairro">Bairro *</Label>
          <Input
            id="bairro"
            name="bairro"
            type="text"
            placeholder="Centro"
            value={formData.bairro}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      {/* Cidade */}
      <div className="space-y-2">
        <Label htmlFor="cidade">Cidade *</Label>
        <Input
          id="cidade"
          name="cidade"
          type="text"
          placeholder="São Paulo"
          value={formData.cidade}
          onChange={handleChange}
          required
        />
      </div>

      {/* Informações adicionais */}
      <div className="space-y-2">
        <Label htmlFor="informacoes_adicionais">Informações adicionais</Label>
        <Input
          id="informacoes_adicionais"
          name="informacoes_adicionais"
          type="text"
          placeholder="Ponto de referência, etc."
          value={formData.informacoes_adicionais}
          onChange={handleChange}
        />
      </div>

      {/* Botões */}
      <div className="flex gap-3 pt-4">
        <Button type="submit" disabled={saving} className="flex-1">
          {saving ? 'Salvando...' : 'Salvar Endereço'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </form>
  );
};

/**
 * Exporta o componente Checkout como padrão
 *
 * O componente Checkout é a página principal de finalização de pedidos
 * da aplicação Conecta-Loja, oferecendo uma experiência completa e moderna.
 *
 * @exports default
 * @type {React.Component}
 */
export default Checkout;
